import { useState, useEffect } from "react";
import { RefreshCw, Users, Trash2 } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import { fmtDateTime, fmtDate } from "../utils/helpers";
import Card from "../components/Card";
import StatusBadge from "../components/StatusBadge";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import type { List, Recipient } from "../types";

export default function ListsPage() {
  const { toast } = useToast();
  const [lists, setLists] = useState<List[]>([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  // Members modal
  const [membersModal, setMembersModal] = useState(false);
  const [activeList, setActiveList] = useState<List | null>(null);
  const [members, setMembers] = useState<Recipient[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersPage, setMembersPage] = useState(1);
  const membersPageSize = 25;

  const fetchLists = async () => {
    try {
      const res = await api.get("/lists");
      if (res.data.ok) setLists(res.data.lists || []);
    } catch {}
  };

  useEffect(() => { fetchLists(); }, []);

  const createList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const res = await api.post("/lists", { name: name.trim(), description: desc.trim() || undefined });
      if (res.data.ok) {
        toast("List created", "success");
        setName(""); setDesc("");
        fetchLists();
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  const openMembers = async (list: List) => {
    setActiveList(list);
    setMembersPage(1);
    setMembersModal(true);
    fetchMembers(list.id, 1);
  };

  const fetchMembers = async (listId: number, page: number) => {
    try {
      const res = await api.get("/lists/" + listId + "/members", { params: { page, page_size: membersPageSize } });
      if (res.data.ok) {
        setMembers(res.data.members || []);
        setMembersTotal(res.data.total || 0);
      }
    } catch {}
  };

  const removeMember = async (recipientId: number) => {
    if (!activeList) return;
    try {
      const res = await api.delete("/lists/" + activeList.id + "/members/" + recipientId);
      if (res.data.ok) {
        toast("Member removed", "success");
        fetchMembers(activeList.id, membersPage);
        fetchLists();
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) { toast(err.response?.data?.error || "Error", "error"); }
  };

  useEffect(() => {
    if (activeList) fetchMembers(activeList.id, membersPage);
  }, [membersPage]);

  const membersTotalPages = Math.max(1, Math.ceil(membersTotal / membersPageSize));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Lists</h1>
        <p className="text-zinc-400 text-sm mt-1">Manage recipient lists for campaigns</p>
      </div>

      <Card title="Create List">
        <form onSubmit={createList} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP, March promo" required
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-zinc-400 mb-1 block">Description</label>
            <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="optional"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors" />
          </div>
          <button type="submit" className="px-4 py-2.5 rounded-xl bg-fuchsia-600 text-white font-semibold hover:bg-fuchsia-500 transition-colors">
            Create
          </button>
        </form>
      </Card>

      <Card title="Lists" badge={<span className="text-xs text-zinc-500">{lists.length}</span>} actions={
        <button onClick={fetchLists} className="text-zinc-400 hover:text-white transition-colors"><RefreshCw size={16} /></button>
      }>
        <p className="text-xs text-zinc-500 mb-3">Adding members excludes opted-out recipients automatically.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-left border-b border-zinc-800">
                <th className="pb-3 font-medium">Name</th>
                <th className="pb-3 font-medium w-28">Members</th>
                <th className="pb-3 font-medium w-28">Opted out</th>
                <th className="pb-3 font-medium w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lists.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-zinc-600 py-8">No lists yet</td></tr>
              ) : lists.map((l) => (
                <tr key={l.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="py-2.5">
                    <div className="font-medium text-white">{l.name}</div>
                    {l.description && <div className="text-xs text-zinc-500">{l.description}</div>}
                  </td>
                  <td className="py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-300">{l.member_count}</span>
                  </td>
                  <td className="py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/10 text-red-400">{l.opted_out_count}</span>
                  </td>
                  <td className="py-2.5 text-right">
                    <button onClick={() => openMembers(l)} className="text-xs px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors">
                      <Users size={14} className="inline mr-1" /> View members
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Members Modal */}
      <Modal open={membersModal} onClose={() => setMembersModal(false)} title={(activeList?.name || "List") + " members"} wide>
        <div>
          <div className="text-sm text-zinc-400 mb-3">{membersTotal} total members</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-left border-b border-zinc-800">
                  <th className="pb-3 font-medium">Phone</th>
                  <th className="pb-3 font-medium">Name</th>
                  <th className="pb-3 font-medium w-24">Opted out</th>
                  <th className="pb-3 font-medium w-24 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-zinc-600 py-8">No members</td></tr>
                ) : members.map((m) => (
                  <tr key={m.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-2.5 font-mono text-xs">{m.phone}</td>
                    <td className="py-2.5">{[m.name, m.last_name].filter(Boolean).join(" ") || "—"}</td>
                    <td className="py-2.5"><StatusBadge status={m.opted_out ? "yes" : "no"} /></td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => removeMember(m.id)} className="text-red-400 hover:text-red-300 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={membersPage} totalPages={membersTotalPages} total={membersTotal}
            onPrev={() => setMembersPage((p) => Math.max(1, p - 1))} onNext={() => setMembersPage((p) => p + 1)} />
        </div>
      </Modal>
    </div>
  );
}
